import {
  WorkspaceVersionSchema,
  workspaceNameSchema
} from '@stratumn/studio-shared';
import { useForm } from '@tanstack/react-form';
import { toast } from 'sonner';
import { z } from 'zod';
import { ColorPicker } from '@/atoms/color/color-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { VersionPicker } from './versionPicker.workspace';

const workspaceFormSchema = z.object({
  name: workspaceNameSchema,
  color: z.string(),
  version: WorkspaceVersionSchema.nullable()
});

export type WorkspaceFormValues = z.infer<typeof workspaceFormSchema>;

type Props = {
  processId: string;
  defaultValues: WorkspaceFormValues;
  onSubmit: (values: WorkspaceFormValues) => void | Promise<void>;
  onCancel: () => void;
  submitLabel: string;
  autoFocusName?: boolean;
};

export const WorkspaceForm = ({
  processId,
  defaultValues,
  onSubmit,
  onCancel,
  submitLabel,
  autoFocusName
}: Props) => {
  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: workspaceFormSchema
    },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
    onSubmitInvalid: ({ formApi }) => {
      const errors = formApi.state.errorMap.onSubmit;
      console.error('[WorkspaceForm] Validation failed', {
        values: formApi.state.values,
        errors
      });
      if (errors) {
        const errorList = Array.isArray(errors) ? errors : [errors];
        const messages = errorList
          .flatMap(e => {
            if (e && typeof e === 'object' && 'message' in e) {
              return [e.message];
            }
            if (typeof e === 'string') {
              return [e];
            }
            return [];
          })
          .filter(Boolean);

        toast.error(
          messages.length > 0 ? messages.join(', ') : 'Invalid form values'
        );
      }
    }
  });

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={e => {
        e.preventDefault();
        e.stopPropagation();
        form.handleSubmit();
      }}
    >
      <form.Field name="name">
        {field => (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <form.Field name="color">
                {colorField => (
                  <ColorPicker
                    color={colorField.state.value}
                    onColorChange={v => colorField.handleChange(v)}
                  />
                )}
              </form.Field>
              <Input
                autoFocus={autoFocusName}
                className="h-7 w-48 text-sm"
                name={field.name}
                onBlur={field.handleBlur}
                onChange={e => field.handleChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    onCancel();
                  }
                }}
                placeholder="Workspace name"
                value={field.state.value}
              />
              <form.Field name="version">
                {versionField => (
                  <VersionPicker
                    compact
                    error={versionField.state.meta.errors.length > 0}
                    onChange={v => versionField.handleChange(v)}
                    processId={processId}
                    value={versionField.state.value}
                  />
                )}
              </form.Field>
            </div>
            <form.Field name="version">
              {versionField => {
                const nameErrors = field.state.meta.errors.flatMap(e => {
                  if (e && typeof e === 'object' && 'message' in e) {
                    return [e.message];
                  }
                  if (typeof e === 'string') {
                    return [e];
                  }
                  return [];
                });
                const versionErrors = versionField.state.meta.errors.flatMap(
                  e => {
                    if (e && typeof e === 'object' && 'message' in e) {
                      return [e.message];
                    }
                    if (typeof e === 'string') {
                      return [e];
                    }
                    return [];
                  }
                );
                const allErrors = [...nameErrors, ...versionErrors];
                if (allErrors.length === 0) {
                  return null;
                }
                return (
                  <p className="text-destructive text-xs">
                    {allErrors.join(', ')}
                  </p>
                );
              }}
            </form.Field>
          </div>
        )}
      </form.Field>
      <form.Subscribe selector={state => state.canSubmit}>
        {canSubmit => (
          <div className="flex justify-end gap-2">
            <Button
              onClick={onCancel}
              size="sm"
              type="button"
              variant="outline"
            >
              Cancel
            </Button>
            <Button disabled={!canSubmit} size="sm" type="submit">
              {submitLabel}
            </Button>
          </div>
        )}
      </form.Subscribe>
    </form>
  );
};
